import { useEffect, useState } from 'react';
import {
  CloudOff,
  Container,
  LogOut,
  Menu,
  PanelLeftClose,
  PanelLeftOpen,
  Search,
  X,
} from 'lucide-react';
import { cn } from '../../lib/cn';
import { useAuth } from '../../context/AuthContext';
import { useLocalStorageState } from '../../hooks/useLocalStorageState';
import { useOnlineStatus } from '../../hooks/useOnlineStatus';
import { NAV_ITEMS, ROUTE_LABELS } from '../../constants/navigation';
import { IconButton, ThemeToggle, Tooltip } from '../ui';

// Rama aplikacji: panel boczny, górna belka i obszar treści.
//
// Względem poprzedniego układu zmieniły się trzy rzeczy poza samym wyglądem:
//  - na wąskim ekranie panel boczny nie znika bez śladu (był `hidden md:flex`,
//    przez co na telefonie nie dało się przejść między widokami inaczej niż
//    jednym przyciskiem "Pulpit") — jest wysuwana szuflada,
//  - stan zwinięcia panelu przeżywa odświeżenie strony,
//  - w belce jest przycisk wyszukiwania z widocznym skrótem klawiaturowym.

function NavList({ route, onNavigate, collapsed }) {
  return (
    <nav className="flex-1 space-y-0.5 p-2">
      {NAV_ITEMS.map(({ id, label, icon: Icon }) => {
        const isActive = route === id;

        const button = (
          <button
            key={id}
            type="button"
            onClick={() => onNavigate(id)}
            aria-current={isActive ? 'page' : undefined}
            className={cn(
              'flex h-9 w-full items-center gap-2.5 rounded-md px-2.5 text-sm font-medium transition-colors',
              isActive
                ? 'bg-accent-soft text-accent'
                : 'text-muted hover:bg-elevated hover:text-content',
              collapsed && 'justify-center px-0'
            )}
          >
            <Icon size={16} className="shrink-0" />
            {!collapsed && <span className="truncate">{label}</span>}
          </button>
        );

        // Przy zwiniętym panelu widoczna jest sama ikona — bez podpowiedzi
        // nie dałoby się rozpoznać pozycji menu.
        return collapsed ? (
          <Tooltip key={id} label={label} side="bottom">
            {button}
          </Tooltip>
        ) : (
          button
        );
      })}
    </nav>
  );
}

function Brand({ collapsed, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex min-w-0 items-center gap-2.5 rounded-md text-left"
    >
      <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-accent text-accent-content">
        <Container size={15} />
      </span>
      {!collapsed && (
        <span className="min-w-0">
          <span className="block truncate text-sm font-semibold leading-tight text-content">
            MGDPT.PL
          </span>
          <span className="block truncate text-2xs leading-tight text-subtle">Container depot</span>
        </span>
      )}
    </button>
  );
}

export function AppShell({ route, onNavigate, onOpenSearch, children }) {
  const { user, logout } = useAuth();
  const isOnline = useOnlineStatus();
  const [collapsed, setCollapsed] = useLocalStorageState('mgdpt-sidebar-collapsed', false);
  const [mobileOpen, setMobileOpen] = useState(false);

  // Zamknięcie szuflady po wybraniu widoku — inaczej zasłaniałaby treść,
  // do której użytkownik właśnie przeszedł.
  useEffect(() => {
    setMobileOpen(false);
  }, [route]);

  const handleNavigate = (id) => onNavigate(id);

  return (
    <div className="flex min-h-screen bg-app">
      {/* Panel boczny — desktop */}
      <aside
        className={cn(
          'hidden shrink-0 flex-col border-r border-line bg-surface transition-[width] duration-200 md:flex',
          collapsed ? 'w-[3.75rem]' : 'w-60'
        )}
      >
        <div
          className={cn(
            'flex h-14 shrink-0 items-center border-b border-line px-3',
            collapsed ? 'justify-center' : 'justify-between'
          )}
        >
          <Brand collapsed={collapsed} onClick={() => handleNavigate(NAV_ITEMS[0].id)} />
          {!collapsed && (
            <IconButton
              icon={PanelLeftClose}
              label="Zwiń panel"
              size="sm"
              onClick={() => setCollapsed(true)}
            />
          )}
        </div>

        <NavList route={route} onNavigate={handleNavigate} collapsed={collapsed} />

        <div className="border-t border-line p-2">
          {collapsed ? (
            <div className="flex flex-col items-center gap-1">
              <IconButton
                icon={PanelLeftOpen}
                label="Rozwiń panel"
                size="sm"
                onClick={() => setCollapsed(false)}
              />
              <IconButton icon={LogOut} label="Wyloguj" tone="danger" size="sm" onClick={logout} />
            </div>
          ) : (
            <>
              <div className="mb-2 rounded-md border border-line bg-elevated px-2.5 py-2">
                <p className="truncate text-xs font-medium text-content" title={user?.email || ''}>
                  {user?.email || 'Użytkownik'}
                </p>
                <p className="mt-0.5 text-2xs text-subtle">Zalogowany</p>
              </div>
              <button
                type="button"
                onClick={logout}
                className="flex h-8 w-full items-center gap-2 rounded-md px-2.5 text-xs font-medium text-danger transition-colors hover:bg-danger-soft"
              >
                <LogOut size={14} />
                Wyloguj
              </button>
            </>
          )}
        </div>
      </aside>

      {/* Szuflada nawigacji — mobile */}
      {mobileOpen && (
        <div className="fixed inset-0 z-40 md:hidden">
          <div
            className="absolute inset-0 animate-fade-in bg-slate-950/60 backdrop-blur-sm"
            onClick={() => setMobileOpen(false)}
          />
          <aside className="absolute inset-y-0 left-0 flex w-64 flex-col border-r border-line bg-surface shadow-pop">
            <div className="flex h-14 shrink-0 items-center justify-between border-b border-line px-3">
              <Brand collapsed={false} onClick={() => handleNavigate(NAV_ITEMS[0].id)} />
              <IconButton icon={X} label="Zamknij menu" size="sm" onClick={() => setMobileOpen(false)} />
            </div>

            <NavList route={route} onNavigate={handleNavigate} collapsed={false} />

            <div className="border-t border-line p-2">
              <p className="mb-2 truncate px-2.5 text-xs text-muted">{user?.email}</p>
              <button
                type="button"
                onClick={logout}
                className="flex h-8 w-full items-center gap-2 rounded-md px-2.5 text-xs font-medium text-danger transition-colors hover:bg-danger-soft"
              >
                <LogOut size={14} />
                Wyloguj
              </button>
            </div>
          </aside>
        </div>
      )}

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-30 flex h-14 shrink-0 items-center justify-between gap-3 border-b border-line bg-surface/90 px-3 backdrop-blur md:px-4">
          <div className="flex min-w-0 items-center gap-2">
            <IconButton
              icon={Menu}
              label="Menu"
              className="md:hidden"
              onClick={() => setMobileOpen(true)}
            />
            <h1 className="truncate text-sm font-semibold text-content">
              {ROUTE_LABELS[route] || 'Widok'}
            </h1>
          </div>

          <div className="flex shrink-0 items-center gap-2">
            {/* Praca offline jest możliwa dzięki trwałej pamięci podręcznej
                Firestore — właśnie dlatego trzeba to zaznaczyć. Bez tego
                operator nie odróżni danych bieżących od danych z dysku. */}
            {!isOnline && (
              <span
                role="status"
                title="Brak połączenia. Zmiany zapisują się lokalnie i wyślą się automatycznie po odzyskaniu sieci."
                className="inline-flex h-8 items-center gap-1.5 rounded-md border border-warn-line bg-warn-soft px-2 text-2xs font-medium text-warn"
              >
                <CloudOff size={13} />
                <span className="hidden sm:inline">Tryb offline</span>
              </span>
            )}

            <button
              type="button"
              onClick={onOpenSearch}
              className="inline-flex h-8 items-center gap-2 rounded-md border border-line bg-elevated px-2.5 text-xs text-muted transition-colors hover:border-line-strong hover:text-content"
            >
              <Search size={14} />
              <span className="hidden sm:inline">Szukaj kontenera…</span>
              <kbd className="hidden rounded border border-line bg-surface px-1 py-px font-sans text-2xs text-subtle sm:inline">
                Ctrl K
              </kbd>
            </button>

            <ThemeToggle />
          </div>
        </header>

        <main className="min-w-0 flex-1 p-3 md:p-4">{children}</main>
      </div>
    </div>
  );
}

export default AppShell;
